import { useQuery } from "@tanstack/react-query";
import { TrendingDown, TrendingUp } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartAreaSkeleton } from "@/components/loading";
import { SearchActivityOverlay } from "@/components/search-activity-overlay";
import { VisibilityScoreTooltip } from "@/components/tooltips/visibility-score-tooltip";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { CHART_COLORS, MAIN_BRAND_COLOR, resolveCssColor } from "@/lib/chart-colors";
import { getDisplayPeriodQuery } from "@/lib/display-period";
import { EMPTY_CHART_MESSAGE } from "@/lib/empty-state-copy";
import { shouldShowSearchActivityOverlay, type SearchExecutionActivity } from "@/lib/search-activity";
import { useTRPC } from "@/lib/trpc";
import { cn, toTitleCase } from "@/lib/utils";
import type { BrandDashboardPeriod } from "@/state/store/userPreferences";
import { calculateTrendFromPreviousPoint } from "../../trend-utils";
import type { ChartBrand, ChartDataPoint } from "../../types";
import { CompetitorsPanel } from "./competitors-panel";
const EMPTY_HIDDEN_BRANDS = new Set<string>();
const TOOLTIP_VIEWPORT_EDGE_PADDING = 16;

interface VisibilityScoreChartProps {
      brandId: string;
      period: BrandDashboardPeriod;
      activity?: SearchExecutionActivity | null;
      highlightedCompetitorId?: string | null;
      showCompetitors?: boolean;
      hiddenCompetitorIds?: ReadonlySet<string>;
      onShowCompetitorsChange?: (show: boolean) => void;
      onToggleCompetitorVisibility?: (brandId: string) => void;
      onCompetitorHoverStart?: (brandId: string) => void;
      onCompetitorHoverEnd?: (brandId?: string) => void;
}

// Competitor colors (all colors except main brand color)
const COMPETITOR_COLORS = CHART_COLORS.slice(1);
export const VisibilityScoreChart = ({
      brandId,
      period,
      activity = null,
      highlightedCompetitorId = null,
      showCompetitors = true,
      hiddenCompetitorIds = EMPTY_HIDDEN_BRANDS,
      onShowCompetitorsChange,
      onToggleCompetitorVisibility,
      onCompetitorHoverStart,
      onCompetitorHoverEnd,
}: VisibilityScoreChartProps) => {
      const showChartLoadingOverlay = shouldShowSearchActivityOverlay(activity);
      const trpc = useTRPC();
      const chartFrameRef = useRef<HTMLDivElement | null>(null);
      const [reverseTooltipX, setReverseTooltipX] = useState(false);

      const { data: chartResponse, isLoading } = useQuery(
            trpc.charts.getDashboardMainChartData.queryOptions({ brandId, ...getDisplayPeriodQuery(period) }),
      );
      const { data: brandRollupMetrics } = useQuery(
            trpc.charts.getBrandRollupMetrics.queryOptions(
                  { brandId, ...getDisplayPeriodQuery(period) },
                  { enabled: !!brandId },
            ),
      );

      // Fetch competitors with metrics for the selected period
      const { data: competitorsWithMetrics } = useQuery(
            trpc.competitors.getCompetitorsWithMetrics.queryOptions(
                  { parentBrandId: brandId, ...getDisplayPeriodQuery(period) },
                  { enabled: !!brandId },
            ),
      );

      // Transform chart data - convert brandScores array to keyed object for recharts
      const chartData = useMemo<ChartDataPoint[]>(() => {
            if (!chartResponse?.chartData) return [];
            return chartResponse.chartData.map((point) => ({
                  date: point.date,
                  label: point.label,
                  intervalLabel: point.intervalLabel,
                  ...Object.fromEntries((point.brandScores ?? []).map((bs) => [bs.brandId, bs.score])),
            }));
      }, [chartResponse]);

      // Get brands list (from chart data)
      const brands = useMemo<ChartBrand[]>(() => {
            return (chartResponse?.brands ?? []).map((brand) => ({
                  id: brand.id,
                  name: brand.name,
                  logo: brand.logo,
                  isMainBrand: brand.isMainBrand,
            }));
      }, [chartResponse]);

      // Use competitorsWithMetrics to populate the panel so newly created competitors appear
      const competitorsForPanel = useMemo<ChartBrand[]>(() => {
            if (!competitorsWithMetrics?.length) {
                  return brands.filter((b) => !b.isMainBrand).slice(0, 5);
            }
            return competitorsWithMetrics
                  .slice()
                  .sort((a, b) => {
                        const aOrder = a.order ?? Number.MAX_SAFE_INTEGER;
                        const bOrder = b.order ?? Number.MAX_SAFE_INTEGER;
                        if (aOrder !== bOrder) return aOrder - bOrder;
                        return (b.visibilityScore ?? 0) - (a.visibilityScore ?? 0);
                  })
                  .slice(0, 5)
                  .map((competitor) => ({
                        id: competitor.brandId,
                        name: competitor.brandName,
                        logo: competitor.brandLogo,
                        isMainBrand: false,
                  }));
      }, [competitorsWithMetrics, brands]);

      // Separate main brand and competitors
      const mainBrand = useMemo(() => brands.find((b) => b.isMainBrand), [brands]);
      const competitors = useMemo(() => brands.filter((b) => !b.isMainBrand), [brands]);
      const highlightedBrandId =
            highlightedCompetitorId &&
            showCompetitors &&
            !hiddenCompetitorIds.has(highlightedCompetitorId) &&
            competitors.some((brand) => brand.id === highlightedCompetitorId)
                  ? highlightedCompetitorId
                  : null;

      // Get color for a brand (include competitors from metrics so new ones get a color)
      const getBrandColor = useMemo(() => {
            const resolvedMainBrandColor = resolveCssColor(MAIN_BRAND_COLOR);
            const resolvedCompetitorColors = COMPETITOR_COLORS.map(resolveCssColor);
            let competitorIndex = 0;
            const colorMap = new Map<string, string>();

            const allBrands = [...brands, ...competitorsForPanel.filter((c) => !brands.some((b) => b.id === c.id))];

            allBrands.forEach((brand) => {
                  if (brand.isMainBrand) {
                        colorMap.set(brand.id, resolvedMainBrandColor);
                  } else {
                        colorMap.set(
                              brand.id,
                              resolvedCompetitorColors[competitorIndex % resolvedCompetitorColors.length],
                        );
                        competitorIndex++;
                  }
            });

            return (brandId: string) => colorMap.get(brandId) ?? resolvedMainBrandColor;
      }, [brands, competitorsForPanel]);

      const resolvedMainBrandColor = useMemo(() => resolveCssColor(MAIN_BRAND_COLOR), []);

      // Calculate trend for main brand
      const trend = useMemo(() => {
            if (chartData.length < 2 || !mainBrand) return null;
            return calculateTrendFromPreviousPoint({
                  chartData,
                  brandId: mainBrand.id,
            });
      }, [chartData, mainBrand]);

      // Check if a line should be visible
      const isLineVisible = (brand: ChartBrand): boolean => {
            if (brand.isMainBrand) return true; // Main brand is always visible
            if (!showCompetitors) return false;
            return !hiddenCompetitorIds.has(brand.id);
      };

      // Check if there's any data
      const hasData = chartData.some((point) =>
            brands.some((brand) => {
                  const value = point[brand.id];
                  return typeof value === "number" && value > 0;
            }),
      );

      // Generate unique gradient ID
      const gradientId = useMemo(() => `visibility-gradient-${Math.random().toString(36).substr(2, 9)}`, []);

      // Calculate Y-axis max: max value + 5%, capped at 100%
      const yAxisMax = useMemo(() => {
            if (chartData.length === 0) return 100;

            let maxValue = 0;
            for (const point of chartData) {
                  for (const brand of brands) {
                        const value = point[brand.id];
                        if (typeof value === "number" && value > maxValue) {
                              maxValue = value;
                        }
                  }
            }

            // Add 5% buffer, but never exceed 100%
            return Math.min(Math.ceil(maxValue + 5), 100);
      }, [chartData, brands]);

      const tooltipWidthEstimate = 430;

      const updateTooltipDirection = (coordinateX?: number) => {
            if (!chartFrameRef.current || typeof coordinateX !== "number" || typeof window === "undefined") {
                  setReverseTooltipX(false);
                  return;
            }

            const chartLeft = chartFrameRef.current.getBoundingClientRect().left;
            const cursorViewportX = chartLeft + coordinateX;
            const spaceToLeft = cursorViewportX - TOOLTIP_VIEWPORT_EDGE_PADDING;
            const spaceToRight = window.innerWidth - cursorViewportX - TOOLTIP_VIEWPORT_EDGE_PADDING;

            setReverseTooltipX(spaceToRight < tooltipWidthEstimate && spaceToLeft >= spaceToRight);
      };

      return (
            <Card
                  className="relative flex min-h-[26rem] w-full flex-col gap-0 overflow-visible rounded-xl p-0 shadow-sm lg:h-[343px]"
            >
                  {/* Row 1: Header - Title + Your Brand */}
                  <div className="flex min-h-14 shrink-0 flex-col border-b lg:h-14 lg:flex-row">
                        {/* Left: Title */}
                        <div className="flex min-h-14 items-center border-b px-4 sm:px-6 lg:h-full lg:flex-1 lg:border-b-0 lg:border-r">
                              <div className="flex items-center gap-3">
                                    <span className="text-lg font-semibold sm:text-xl">Visibility Score</span>
                                    <VisibilityScoreTooltip />
                              </div>
                        </div>

                        {/* Right: Main Brand + trend */}
                        {mainBrand && (
                              <div className="flex min-h-14 items-center px-4 py-3 lg:h-full lg:min-w-[32%] lg:py-0">
                                    <div className="flex w-full flex-wrap items-center gap-3 sm:flex-nowrap">
                                          <div
                                                className="w-3 h-3 rounded-full shrink-0"
                                                style={{ backgroundColor: resolvedMainBrandColor }}
                                          />
                                          {mainBrand.logo && (
                                                <img
                                                      src={mainBrand.logo}
                                                      alt={toTitleCase(mainBrand.name)}
                                                      className="w-6 h-6 shrink-0 object-cover rounded-lg"
                                                      onError={(e) => {
                                                            e.currentTarget.style.display = "none";
                                                      }}
                                                />
                                          )}
                                          <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium truncate">
                                                      {toTitleCase(mainBrand.name)}
                                                </div>
                                                <div className="text-xs text-muted-foreground">Your brand</div>
                                          </div>
                                          <div className="flex shrink-0 items-center gap-2">
                                                {trend && (
                                                      <Badge
                                                            variant="outline"
                                                            className={cn(
                                                                  trend.isPositive
                                                                        ? "text-success bg-success-light border-success/20"
                                                                        : "text-destructive bg-destructive/10 border-destructive/20",
                                                            )}
                                                      >
                                                            {trend.isPositive ? (
                                                                  <TrendingUp className="h-3 w-3 mr-1" />
                                                            ) : (
                                                                  <TrendingDown className="h-3 w-3 mr-1" />
                                                            )}
                                                            {trend.value}%
                                                      </Badge>
                                                )}
                                                <div
                                                      className="text-lg font-semibold tabular-nums sm:text-xl"
                                                      style={{ color: resolvedMainBrandColor }}
                                                >
                                                      {brandRollupMetrics?.visibilityScore?.toFixed(1) ?? "—"}%
                                                </div>
                                          </div>
                                    </div>
                              </div>
                        )}
                  </div>

                  {/* Row 2: Chart + Competitors */}
                  <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
                        {/* Left: Chart */}
                        <div className="relative min-h-[280px] min-w-0 border-b px-3 py-4 sm:px-6 lg:min-h-0 lg:flex-1 lg:border-b-0 lg:border-r">
                              {showChartLoadingOverlay && (
                                    <SearchActivityOverlay activity={activity} showElapsedTime />
                              )}

                              {isLoading ? (
                                    <ChartAreaSkeleton className="border-0 bg-transparent p-0" />
                              ) : !hasData ? (
                                    <div className="flex items-center justify-center h-full text-muted-foreground">
                                          {!showChartLoadingOverlay && EMPTY_CHART_MESSAGE}
                                    </div>
                              ) : (
                                    <div ref={chartFrameRef} className="h-full">
                                          <ResponsiveContainer width="100%" height="100%">
                                                <ComposedChart
                                                      data={chartData}
                                                      margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                                                      onMouseEnter={(state) => {
                                                            updateTooltipDirection(state.activeCoordinate?.x);
                                                      }}
                                                      onMouseLeave={() => setReverseTooltipX(false)}
                                                      onMouseMove={(state) => {
                                                            updateTooltipDirection(state.activeCoordinate?.x);
                                                      }}
                                                >
                                                      <defs>
                                                            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                                                  <stop
                                                                        offset="0%"
                                                                        stopColor={resolvedMainBrandColor}
                                                                        stopOpacity={0.15}
                                                                  />
                                                                  <stop
                                                                        offset="100%"
                                                                        stopColor={resolvedMainBrandColor}
                                                                        stopOpacity={0}
                                                                  />
                                                            </linearGradient>
                                                      </defs>
                                                      <CartesianGrid
                                                            vertical={false}
                                                            horizontal={true}
                                                            strokeDasharray="2 6"
                                                            stroke="var(--border)"
                                                            opacity={0.5}
                                                      />
                                                      <XAxis
                                                            dataKey="label"
                                                            tickLine={false}
                                                            axisLine={false}
                                                            tickMargin={10}
                                                            className="text-xs"
                                                            tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                                                      />
                                                      <YAxis
                                                            tickLine={false}
                                                            axisLine={false}
                                                            tickMargin={8}
                                                            className="text-xs"
                                                            tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                                                            domain={[0, yAxisMax]}
                                                            width={46}
                                                            tickCount={5}
                                                            tickFormatter={(value) => `${value}%`}
                                                      />
                                                      {/* Render competitors first so main brand appears on top */}
                                                      {competitors.map((brand) => (
                                                            <Line
                                                                  key={brand.id}
                                                                  type="monotone"
                                                                  dataKey={brand.id}
                                                                  stroke={getBrandColor(brand.id)}
                                                                  strokeWidth={1.5}
                                                                  strokeLinecap="round"
                                                                  strokeLinejoin="round"
                                                                  opacity={
                                                                        highlightedBrandId &&
                                                                        highlightedBrandId !== brand.id
                                                                              ? 0.15
                                                                              : 0.75
                                                                  }
                                                                  dot={false}
                                                                  activeDot={{
                                                                        r: 3.5,
                                                                        strokeWidth: 2,
                                                                        stroke: "var(--background)",
                                                                        fill: getBrandColor(brand.id),
                                                                  }}
                                                                  hide={!isLineVisible(brand)}
                                                                  connectNulls
                                                            />
                                                      ))}
                                                      {/* Main brand area + line on top */}
                                                      {mainBrand && (
                                                            <Area
                                                                  key={mainBrand.id}
                                                                  type="monotone"
                                                                  dataKey={mainBrand.id}
                                                                  stroke={resolvedMainBrandColor}
                                                                  fill={`url(#${gradientId})`}
                                                                  strokeWidth={2.5}
                                                                  strokeLinecap="round"
                                                                  strokeLinejoin="round"
                                                                  opacity={highlightedBrandId ? 0.18 : 1}
                                                                  dot={false}
                                                                  activeDot={{
                                                                        r: 5,
                                                                        strokeWidth: 2.5,
                                                                        stroke: "var(--background)",
                                                                        fill: resolvedMainBrandColor,
                                                                  }}
                                                                  connectNulls
                                                            />
                                                      )}
                                                      <Tooltip
                                                            allowEscapeViewBox={{ x: true, y: true }}
                                                            content={({ active, payload, label }) => {
                                                                  if (!active || !payload || payload.length === 0)
                                                                        return null;
                                                                  const intervalLabel =
                                                                        (payload[0]?.payload as ChartDataPoint | undefined)
                                                                              ?.intervalLabel ?? label;

                                                                  // Get visible brands sorted: main brand first, then competitors by score
                                                                  const visibleBrands = brands.filter(isLineVisible);
                                                                  const sortedBrands = [
                                                                        ...visibleBrands.filter((b) => b.isMainBrand),
                                                                        ...visibleBrands
                                                                              .filter((b) => !b.isMainBrand)
                                                                              .sort((a, b) => {
                                                                                    const aVal =
                                                                                          (payload.find(
                                                                                                (p) =>
                                                                                                      p.dataKey ===
                                                                                                      a.id,
                                                                                          )?.value as number) ?? 0;
                                                                                    const bVal =
                                                                                          (payload.find(
                                                                                                (p) =>
                                                                                                      p.dataKey ===
                                                                                                      b.id,
                                                                                          )?.value as number) ?? 0;
                                                                                    return bVal - aVal;
                                                                              }),
                                                                  ];

                                                                  return (
                                                                        <div className="bg-popover/95 backdrop-blur-sm border border-border/60 rounded-xl shadow-xl p-3 min-w-[155px] max-w-[calc(100vw-2rem)] sm:max-w-[26rem]">
                                                                              <div className="text-xs font-medium text-muted-foreground mb-2.5 pb-2 border-b border-border/50">
                                                                                    {intervalLabel}
                                                                              </div>
                                                                              <div className="space-y-1.5">
                                                                                    {sortedBrands.map((brand) => {
                                                                                          const dataPoint =
                                                                                                payload.find(
                                                                                                      (p) =>
                                                                                                            p.dataKey ===
                                                                                                            brand.id,
                                                                                                );
                                                                                          const value =
                                                                                                dataPoint?.value as
                                                                                                      | number
                                                                                                      | undefined;

                                                                                          return (
                                                                                                <div
                                                                                                      key={brand.id}
                                                                                                      className="flex items-start gap-4"
                                                                                                >
                                                                                                      <div className="flex min-w-0 flex-1 items-start gap-2">
                                                                                                            <div
                                                                                                                  className="w-1.5 h-1.5 mt-1.5 rounded-full shrink-0"
                                                                                                                  style={{
                                                                                                                        backgroundColor:
                                                                                                                              getBrandColor(
                                                                                                                                    brand.id,
                                                                                                                              ),
                                                                                                                  }}
                                                                                                            />
                                                                                                            <span className="min-w-0 text-xs leading-4 text-foreground/80 break-words">
                                                                                                                  {toTitleCase(
                                                                                                                        brand.name,
                                                                                                                  )}
                                                                                                            </span>
                                                                                                      </div>
                                                                                                      <span
                                                                                                            className="text-xs font-semibold shrink-0 tabular-nums"
                                                                                                            style={{
                                                                                                                  color: brand.isMainBrand
                                                                                                                        ? resolvedMainBrandColor
                                                                                                                        : undefined,
                                                                                                            }}
                                                                                                      >
                                                                                                            {value !=
                                                                                                            null
                                                                                                                  ? `${value.toFixed(0)}%`
                                                                                                                  : "—"}
                                                                                                      </span>
                                                                                                </div>
                                                                                          );
                                                                                    })}
                                                                              </div>
                                                                        </div>
                                                                  );
                                                            }}
                                                            cursor={{
                                                                  stroke: "var(--border)",
                                                                  strokeWidth: 1,
                                                            }}
                                                            reverseDirection={{ x: reverseTooltipX, y: false }}
                                                            wrapperStyle={{ zIndex: 20 }}
                                                      />
                                                </ComposedChart>
                                          </ResponsiveContainer>
                                    </div>
                              )}
                        </div>
                        <CompetitorsPanel
                              competitors={competitorsForPanel}
                              showCompetitors={showCompetitors}
                              setShowCompetitors={(show) => onShowCompetitorsChange?.(show)}
                              competitorsWithMetrics={competitorsWithMetrics ?? []}
                              hiddenBrands={hiddenCompetitorIds}
                              hoveredBrandId={highlightedBrandId}
                              toggleBrandVisibility={(brandId) => onToggleCompetitorVisibility?.(brandId)}
                              onCompetitorHoverStart={onCompetitorHoverStart}
                              onCompetitorHoverEnd={onCompetitorHoverEnd}
                              getBrandColor={getBrandColor}
                              activity={activity}
                        />
                  </div>
            </Card>
      );
};
